'use client';

import { lazy, Suspense, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Box } from '@mui/material';

import { SignallerForm } from '../../components/signallerForm/SignallerForm';
import { handleReport, recaptchaValidation } from '../../handlers/report/report.handler';
import { useExternalScript } from '../../../../../../hooks/externalScripts/externalScript.hook';
import { APP_RECAPTCHA_API_URL } from '../../../../../../configs/recaptcha.config';
import { getConfirmType } from '../../handlers/formState/formState.handler';
import { isPasswordFieldEnabled } from '../../../../../../handlers/showPasswordField/showPasswordField.handler';
import { isClient } from '../../../../../../utils/next/next.util';

import type { TDialogState } from './signallerFormClient.view.type';

const ConfirmDialog = lazy(() =>
  import('../../components/confirmDialog/ConfirmDialog').then((m) => ({
    default: m.ConfirmDialog,
  })),
);

const SuccessDialog = lazy(() =>
  import('../../components/successDialog/SuccessDialog').then((m) => ({
    default: m.SuccessDialog,
  })),
);

const ErrorDialog = lazy(() =>
  import('../../components/errorDialog/ErrorDialog').then((m) => ({
    default: m.ErrorDialog,
  })),
);

const SignallerFormClientView = ({ locale }: { locale: string }) => {
  useExternalScript(APP_RECAPTCHA_API_URL);

  const t = useTranslations();

  const [dialogState, setDialogState] = useState<TDialogState>({ type: '' });

  const { type: dialogType } = dialogState;
  const showPassword = isClient() ? isPasswordFieldEnabled() : true;

  const handleClose = () => {
    setDialogState({ type: '' });
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Suspense>
          <SignallerForm
            locale={locale}
            showPassword={showPassword}
            handleSubmit={async (form, { handleReset, handleSetError, availableSubjects }) => {
              const recaptchaToken = await recaptchaValidation(t, handleSetError);
              const confirmType = getConfirmType(form, showPassword);

              if (recaptchaToken) {
                if (confirmType) {
                  setDialogState({
                    type: confirmType,
                    form,
                    handleReset,
                    handleSetError,
                    availableSubjects,
                    recaptchaToken,
                  });
                } else {
                  await handleReport(
                    { ...form, recaptchaToken },
                    {
                      setModal: setDialogState,
                      t,
                      handleReset,
                      handleSetError,
                      availableSubjects,
                    },
                  );
                }
              }
            }}
          />
        </Suspense>
      </Box>

      {(dialogType === 'confirm' ||
        dialogType === 'confirmPass' ||
        dialogType === 'confirmSignaller') && (
        <Suspense>
          <ConfirmDialog
            confirmType={dialogType}
            handleReport={async () => {
              const { form, handleReset, handleSetError, availableSubjects, recaptchaToken } =
                dialogState;
              await handleReport(
                { ...form, recaptchaToken },
                {
                  setModal: setDialogState,
                  t,
                  handleReset,
                  handleSetError,
                  availableSubjects,
                },
              );
            }}
            handleClose={handleClose}
          />
        </Suspense>
      )}

      {dialogType === 'error' && (
        <Suspense>
          <ErrorDialog handleClose={handleClose} />
        </Suspense>
      )}
      {dialogType === 'success' && (
        <Suspense>
          <SuccessDialog {...dialogState} handleClose={handleClose} />
        </Suspense>
      )}
    </>
  );
};

export default SignallerFormClientView;
